<template>
  <div class="grouping-quiz-overview-component">
    <div class="grouping-header">
      <h2 class="grouping-header-text">
        {{ grouping.name }}
      </h2>
      <p
        v-if="!isNaN(percentCorrect)"
        class="grouping-percent-correct"
      >
        {{ percentCorrect }}% Correct
      </p>
    </div>
    <template v-if="hasUnansweredFlashcards">
      <div class="questions-group">
        <div
          v-for="i in grouping.flashcardAssociations.length"
          :key="i"
          class="question-indicator"
        >
          <div
            v-if="i <= answeredFlashcardsLength"
            class="question-indicator-inner"
          />
        </div>
      </div>
      <p>{{ pluralize('question', answeredFlashcardsLength) }} answered, {{ pluralize('question', unansweredFlashcardsLength) }} remaining.</p>
    </template>
    <template v-else>
      <mastery-question-list
        :questions="flashcards"
        :states="flashcardStates"
        :sort-by-state="false"
      />
    </template>
    <mastery-footer
      :disable-btn="false"
      :primary-action-text="primaryFooterText"
      :secondary-action-text="secondaryFooterText"
      @primary-action="handlePrimaryFooterAction"
      @secondary-action="handleSecondaryFooterAction"
    />
  </div>
</template>

<script>
import MasteryFooter from '@/components/mastery/footer';
import MasteryQuestionList from '@/components/mastery/question-list';

const QUIZ_STATES = {
  NOT_STARTED: 'Not Started',
  IN_PROGRESS: 'In Progress',
  COMPLETE: 'Complete',
};

export default {
  name: 'GroupingQuizOverview',
  components: { MasteryFooter, MasteryQuestionList },
  props: {
    flashcards: {
      type: Array,
    },
    flashcardStates: {
      type: Array,
    },
    grouping: {
      type: Object,
    },
  },
  computed: {
    answeredFlashcardsLength() {
      return this.answeredFlashcardStates?.length || 0;
    },
    answeredFlashcardStates() {
      const unansweredFlashcardStatus = this.$store.getters['flashcardStatuses/unansweredFlashcardStatus'];
      const answeredFlashcardStates = this.flashcardStates.filter(state => state.flashcardStatusId !== unansweredFlashcardStatus?.id);
      return answeredFlashcardStates;
    },
    hasUnansweredFlashcards() {
      return this.unansweredFlashcardsLength > 0;
    },
    percentCorrect() {
      if (this.quizState === QUIZ_STATES.COMPLETE) {
        const correct = this.answeredFlashcardStates.filter(state => state.correct).length;
        return Math.floor((correct / this.answeredFlashcardStates.length) * 100);
      }
    },
    primaryFooterText() {
      switch (this.quizState) {
        case QUIZ_STATES.NOT_STARTED:
          return 'Start';
        case QUIZ_STATES.IN_PROGRESS:
          return 'Continue';
        case QUIZ_STATES.COMPLETE:
          return 'Restart';
        default:
          return undefined;
      }
    },
    quizState() {
      if (this.answeredFlashcardsLength === 0) {
        return QUIZ_STATES.NOT_STARTED;
      } else if (this.unansweredFlashcardsLength > 0) {
        return QUIZ_STATES.IN_PROGRESS;
      } else if (this.grouping.flashcardAssociations.length === this.answeredFlashcardsLength) {
        return QUIZ_STATES.COMPLETE;
      }
    },
    secondaryFooterText() {
      if (this.quizState === QUIZ_STATES.IN_PROGRESS) {
        return 'Restart';
      }
    },
    unansweredFlashcardsLength() {
      return this.grouping.flashcardAssociations.length - this.answeredFlashcardsLength;
    },
  },
  methods: {
    handlePrimaryFooterAction() {
      switch (this.quizState) {
        case QUIZ_STATES.NOT_STARTED:
          this.routeToFirstQuesiton();
          break;
        case QUIZ_STATES.IN_PROGRESS:
          this.routeToFirstUnansweredQuestion();
          break;
        case QUIZ_STATES.COMPLETE:
          this.resetGrouping();
          break;
        default:
          console.log('Yoinks! Something broke');
      }
    },
    handleSecondaryFooterAction() {
      this.resetGrouping();
    },
    pluralize(word, length) {
      if (length === 1) {
        return `${length} ${word}`;
      }
      return `${length} ${word}s`;
    },
    resetGrouping() {
      this.$store.dispatch('groupings/reset', { id: this.grouping.id }).then(() => this.routeToFirstQuesiton());
    },
    routeToFirstQuesiton() {
      this.$router.push({
        name: 'groupings#show',
        params: {
          ...this.$route.params,
          assetAssociationId: this.grouping.sortedAssetAssociations[0].id,
        },
      });
    },
    routeToFirstUnansweredQuestion() {
      const answeredFlashcardIds = this.answeredFlashcardStates.map(state => state.flashcardId);
      const nextAssetAssociation = this.grouping.sortedAssetAssociations.find(aa => !answeredFlashcardIds.includes(aa.assetBId));
      this.$router.push({
        name: 'groupings#show',
        params: {
          ...this.$route.params,
          assetAssociationId: nextAssetAssociation.id,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.grouping-quiz-overview-component {
  margin: rem-calc(64) auto 0;
  max-width: rem-calc(568);

  .grouping-header {
    border-bottom: 1px solid #dbdbdb;
    margin: 0 0 rem-calc(32);
    padding: 0 0 rem-calc(32);

    .grouping-header-text {
      font-size: rem-calc(36);
      margin: 0;
    }

    .grouping-percent-correct {
      font-size: rem-calc(24);
      margin: 0;
      margin: rem-calc(16) 0 0;
    }
  }

  .question-indicator {
    align-items: center;
    border-radius: 100%;
    border: 1px solid #00dbfb;
    color: #00dbfb;
    display: inline-flex;
    font-size: rem-calc(14);
    font-weight: $semi-bold;
    height: rem-calc(24);
    justify-content: center;
    width: rem-calc(24);
    margin-right: rem-calc(8);
    vertical-align: middle;

    .question-indicator-inner {
      background: #00dbfb;
      border-radius: 100%;
      margin: 0;
      padding: 0;
      height: rem-calc(20);
      width: rem-calc(20);
      vertical-align: middle;
    }
  }
}
</style>
